import React from 'react';
import { Loader, Pagination } from '@mantine/core';
import {
  WOLT_DIRECTION_LABELS,
  WOLT_KIND_LABELS,
  WOLT_STATUS_LABELS,
  resolveStatusClass,
} from '../monitoring-page.constants';
import { formatDateTime } from '../monitoring-page.utils';
import type { UseMonitoringPage } from '../useMonitoringPage';
import classes from '../monitoring-page.module.scss';

type WoltCallLogsTableProps = Pick<
  UseMonitoringPage,
  | 'woltRows'
  | 'isWoltLoading'
  | 'hasLoadedWolt'
  | 'woltTotalPages'
  | 'woltPage'
  | 'setWoltPage'
  | 'openWoltDetail'
>;

const statusBadgeClass = (status: string) => {
  if (status === 'success') return classes.levelBadgeMuted;
  if (status === 'timeout') return classes.levelBadgeWarn;
  return classes.levelBadgeError;
};

const httpBadgeClass = (statusClass: '4xx' | '5xx' | null) =>
  statusClass === '5xx' ? classes.statusBadge5xx : statusClass === '4xx' ? classes.statusBadge4xx : classes.levelBadgeMuted;

export const WoltCallLogsTable: React.FC<WoltCallLogsTableProps> = ({
  woltRows,
  isWoltLoading,
  hasLoadedWolt,
  woltTotalPages,
  woltPage,
  setWoltPage,
  openWoltDetail,
}) => {
  if (isWoltLoading && !hasLoadedWolt) {
    return (
      <div className={classes.loadingWrap}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={classes.tableWrap}>
      <table className={classes.table} data-testid="monitoring-wolt-table">
        <thead>
          <tr>
            <th>{'תאריך ושעה'}</th>
            <th>{'עסק'}</th>
            <th>{'כיוון'}</th>
            <th>{'סוג'}</th>
            <th>{'סטטוס'}</th>
            <th>{'קוד HTTP'}</th>
            <th>{'זמן תגובה'}</th>
            <th>{'הזמנת וולט'}</th>
            <th>{'שגיאה'}</th>
          </tr>
        </thead>
        <tbody>
          {woltRows.map((row) => (
            <tr
              key={row.id}
              className={classes.clickableRow}
              role="button"
              tabIndex={0}
              data-testid={`monitoring-wolt-row-${row.id}`}
              onClick={() => openWoltDetail(row)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openWoltDetail(row);
                }
              }}
            >
              <td data-label={'תאריך ושעה'}>{formatDateTime(row.createdAt)}</td>
              <td data-label={'עסק'}>{row.companyName ?? row.tenantId ?? '-'}</td>
              <td data-label={'כיוון'}>{WOLT_DIRECTION_LABELS[row.direction] ?? row.direction}</td>
              <td data-label={'סוג'}>{WOLT_KIND_LABELS[row.kind] ?? row.kind}</td>
              <td data-label={'סטטוס'}>
                <span className={`${classes.detailKindBadge} ${statusBadgeClass(row.status)}`}>
                  {WOLT_STATUS_LABELS[row.status] ?? row.status}
                </span>
              </td>
              <td data-label={'קוד HTTP'}>
                {row.httpStatus != null ? (
                  <span className={`${classes.detailKindBadge} ${httpBadgeClass(resolveStatusClass(row.httpStatus))}`}>
                    {row.httpStatus}
                  </span>
                ) : (
                  '-'
                )}
              </td>
              <td data-label={'זמן תגובה'}>{row.latencyMs != null ? `${row.latencyMs} ms` : '-'}</td>
              <td data-label={'הזמנת וולט'}>{row.woltOrderId ?? '-'}</td>
              <td data-label={'שגיאה'}>
                <div className={classes.errorMessageCell}>{row.errorMessage ?? '-'}</div>
              </td>
            </tr>
          ))}
          {woltRows.length === 0 && (
            <tr>
              <td colSpan={9} className={classes.empty}>
                {'אין רשומות מעקב וולט לסינון הנוכחי.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {woltTotalPages > 1 && (
        <div className={classes.pagination}>
          <Pagination total={woltTotalPages} value={woltPage} onChange={setWoltPage} size="sm" />
        </div>
      )}
    </div>
  );
};
